import { Table, addToast } from '@octano/global-ui';
import { useColumns } from '../hooks/useColumns';
import { TranslationsKeys } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import {
  useClassrooms,
  ITEMS_PER_PAGE_DEFAULT,
} from './Loaders/ClassroomsLoader';
import { RecordToEditBody, ClassroomApiResponse } from '../types';
import { remove, toggleStatus } from '../api';
import ClassroomFormModal, {
  ClassroomFormModalMethods,
} from './modal/ClassroomFormModal';
import ReservationDetailsModal, {
  ReservationDetailsModalMethods,
} from './modal/ReservationDetailsModal';
import DisableDetailsModal, {
  DisableDetailsModalMethods,
} from './modal/DisableDetailsModal';
import useTexts from '../hooks/useTexts';

export default function ClassroomsTable() {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_CLASSROOMS);
  const classroomModalRef = useRef<ClassroomFormModalMethods>(null);
  const reservationDetailsModalRef = useRef<ReservationDetailsModalMethods>(
    null,
  );
  const disableDetailsModalRef = useRef<DisableDetailsModalMethods>(null);
  const { data, query, updateQuery, loading, refresh } = useClassrooms();
  const texts = useTexts();

  const handleEdit = (classroom: ClassroomApiResponse) => {
    const classroomToEdit: RecordToEditBody = {
      id: classroom.id,
      code: classroom.code,
      name: classroom.name,
      capacity: classroom.capacity,
      typeId: classroom.typeId,
      campusId: classroom.campusId,
      isEnabled: classroom.isEnabled,
      campus: {
        id: classroom.campusId,
        name: classroom.campus || '',
      },
      type: {
        id: parseInt(classroom.typeId),
        name: classroom.type || '',
      },
    };

    classroomModalRef.current?.open(classroomToEdit);
  };

  const handleDelete = (classroom: ClassroomApiResponse) => {
    reservationDetailsModalRef.current?.open(classroom);
  };

  const handleDisable = (classroom: ClassroomApiResponse) => {
    disableDetailsModalRef.current?.open(classroom);
  };

  const handleConfirmDelete = async (classroom: ClassroomApiResponse) => {
    try {
      await remove(classroom.id);
      addToast({
        icon: 'success',
        color: 'success',
        text: texts.delete.success,
      });
      refresh();
    } catch (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: texts.form.error,
      });
      throw error;
    }
  };

  const handleConfirmDisable = async (classroom: ClassroomApiResponse) => {
    try {
      await toggleStatus(classroom.id, !classroom.isEnabled);
      addToast({
        icon: 'success',
        color: 'success',
        text: texts.disable.success,
      });
      refresh();
    } catch (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: texts.form.error,
      });
      throw error;
    }
  };

  const columns = useColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onDisable: handleDisable,
  });

  return (
    <>
      <Table
        columns={columns}
        data={data?.data ?? []}
        isLoadingResults={loading}
        pagination={{
          currentPage: query.page,
          itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
          totalPages: data?.total_pages ?? 0,
          onChangePage: (page) => updateQuery({ ...query, page }),
          totalItems: data?.total ?? 0,
        }}
        noResultsText={t('table.noResults')}
      />

      <ClassroomFormModal ref={classroomModalRef} onSave={refresh} />
      <ReservationDetailsModal
        ref={reservationDetailsModalRef}
        onConfirmDelete={handleConfirmDelete}
      />
      <DisableDetailsModal
        ref={disableDetailsModalRef}
        onConfirmDisable={handleConfirmDisable}
      />
    </>
  );
}
